import { generateUuid, handleMessageError } from "@lhd/features/Apps/utils";
import { getCompanyId, isBlankOrNullStatus } from "@lhd/utility";
import { Checkbox, Input, notification } from "antd";
import {
  useDeleteCategoryStatus,
  useFetchCategoryStatuses,
  useRestoreCategoryStatus,
  useSubmitCategoryStatus,
} from "@lhd/hooks";
import { useEffect, useRef, useState } from "react";
import { CategoryStatus, DISPLAY_SIZES } from "@lhd/types";
import Button from "@lhd/components/Button";
import { useSelector } from "react-redux";
import { getDisplaySize } from "@lhd/components/DisplaySizeSelector/selector";
import { BUTTON_HEIGHT, FONT_SIZE, FONT_SIZE_VALUE } from "@lhd/constants";

export type ReceivedProps = Record<string, any>;

export const displayCategoryStatusSettingsOptions = [
  { value: "is_hidden", label: "表示" },
  { value: "is_auto_select", label: "初期選択" },
];

export const checkCategoryStatusIsInprogressing = (status: string) =>
  status?.includes("全社展開中") ||
  status?.includes("一時削除中") ||
  status?.includes("復元中") ||
  status?.includes("選択肢削除中");

// Check if a category/status record represents the empty / null option
export const isOptionNull = (record?: CategoryStatus | any): boolean => {
  if (!record) return false;
  const name = record.name?.trim?.() ?? record.name;
  const value =
    record.value !== undefined && record.value !== null
      ? String(record.value).trim()
      : undefined;
  const id =
    record.id !== undefined && record.id !== null
      ? String(record.id).trim()
      : undefined;

  return (
    name === "__NULL__" ||
    name === "(空白)" ||
    name === "NULL" ||
    value === "__NULL__" ||
    value === "(空白)" ||
    value === "NULL" ||
    id === "__NULL__" ||
    id === "NULL" ||
    isBlankOrNullStatus(record)
  );
};

const useCategoryStatusTable = (props: ReceivedProps) => {
  const { screen, recordType, nonEditable } = props;

  const [dataSource, setDataSource] = useState<CategoryStatus[]>([]); // id = uuid => is create new
  const [modalConfirm, setModalConfirm] = useState<{
    isOpen: boolean;
    callback: any;
    content: string;
  }>({
    isOpen: false,
    callback: null,
    content: "",
  });
  const [modalEditConfirm, setModalEditConfirm] = useState<{
    isOpen: boolean;
    recordId: any;
    originalName: string;
    newName: string;
    updateRelatedData: boolean;
    saveType: "current_company" | "all_companies";
  }>({
    isOpen: false,
    recordId: null,
    originalName: "",
    newName: "",
    updateRelatedData: false,
    saveType: "current_company",
  });
  const saveTypeRef = useRef<"current_company" | "all_companies">(
    "current_company",
  );

  const displaySize = useSelector(getDisplaySize) as
    | keyof typeof FONT_SIZE
    | keyof typeof BUTTON_HEIGHT;
  const height = BUTTON_HEIGHT[displaySize] ?? BUTTON_HEIGHT.small;
  const size = FONT_SIZE_VALUE[displaySize ?? DISPLAY_SIZES.SMALL];

  const {
    data: categoryStatusesData,
    isLoading,
    isFetching,
    isSuccess,
    refetch,
  } = useFetchCategoryStatuses(1, 10000, {
    screen,
    record_type: recordType,
  });

  const { mutate, isLoading: isSubmitting } = useSubmitCategoryStatus();
  const { mutate: deleteCategoryStatus, isLoading: isDeletingCategoryStatus } =
    useDeleteCategoryStatus();
  const {
    mutate: restoreCategoryStatus,
    isLoading: isRestoringCategoryStatus,
  } = useRestoreCategoryStatus();

  const handleChangeData = (
    fieldName: keyof CategoryStatus,
    id: any,
    value: any,
  ) => {
    setDataSource((oldData) =>
      [...oldData].map((item) =>
        item.id === id ? { ...item, [fieldName]: value } : item,
      ),
    );
  };

  const handleAddNewRow = () => {
    setDataSource((oldData) => [
      ...oldData,
      {
        id: generateUuid(),
        company_id: getCompanyId(),
        name: "",
        deleted_at: null,
        record_type: recordType,
        is_hidden: false,
        is_auto_select: false,
        is_default: false,
        status: "",
        is_global: false,
        screen: screen,
      },
    ]);
  };

  const handleDeleteRow = (id: any) => {
    // case create new (not save to DB yet)
    if (isNaN(Number(id))) {
      setDataSource((oldData) => [...oldData]?.filter((i) => i?.id !== id));
    } else {
      const record = dataSource?.find((i) => i?.id === id);

      // Do not allow deleting NULL option
      if (isOptionNull(record)) {
        notification["error"]({
          message: "この項目は削除できません。",
        });
        onCloseModalConfirm();
        return;
      }

      // Check if record can be deleted
      if (record?.can_be_deleted === false) {
        notification["error"]({
          message: "現在使用中のため、削除できません。",
        });
        onCloseModalConfirm();
        return;
      }

      deleteCategoryStatus(
        {
          id,
          is_hard_delete: !!record?.deleted_at,
        },
        {
          onError(e: any) {
            notification["error"]({
              message: handleMessageError(e?.response?.data?.msg),
            });
          },
          onSettled() {
            onCloseModalConfirm();
          },
        },
      );
    }
  };

  const handleDeployment = (id: any) => {
    const listName = dataSource
      ?.filter((i) => i?.id !== id)
      ?.map((i) => i?.name);
    const recordData = dataSource?.find((i) => i?.id === id);
    const recordOriginal = categoryStatusesData?.data?.find(
      (i) => i?.id === id,
    );

    if (!recordData?.name) {
      notification["error"]({
        message: "必須項目です: 項目名",
      });
      return;
    } else if (recordData && listName?.includes(recordData?.name)) {
      notification["error"]({
        message: "現在同一名称があります。再度確認してください。",
      });
      return;
    }

    // Check if name has changed and can_be_deleted is false
    if (
      recordData?.can_be_deleted === false &&
      recordOriginal?.name !== recordData?.name
    ) {
      // Show confirmation modal before saving
      saveTypeRef.current = "current_company";
      setModalEditConfirm((prev) => ({
        ...prev,
        isOpen: true,
        recordId: id,
        originalName: recordOriginal?.name || "",
        newName: recordData?.name,
        updateRelatedData: true,
        saveType: "current_company",
      }));
      return;
    }

    onApplyForCompany(id, "current_company");
  };

  const handleRestore = (id: any) => {
    restoreCategoryStatus(
      {
        id,
      },
      {
        onError(e: any) {
          notification["error"]({
            message: handleMessageError(e?.response?.data?.msg),
          });
        },
        onSettled() {
          onCloseModalConfirm();
        },
      },
    );
  };

  const onApplyForCompany = (
    id: string,
    save_type: "current_company" | "all_companies",
    newName?: string,
    updateRelatedData = false,
  ) => {
    const record = dataSource?.find((i) => i?.id === id);

    if (!record) {
      notification["error"]({
        message: "レコードが見つかりません。",
      });
      return;
    }

    // Build payload exactly like normal update
    // Update name if newName is provided (from modal confirm)
    const payload: any = {
      name: newName ?? record?.name ?? "",
      record_type: record?.record_type ?? recordType,
      screen: record?.screen ?? screen,
      is_hidden: record?.is_hidden ?? false,
      is_auto_select: record?.is_auto_select ?? false,
      is_default: record?.is_default ?? false,
      save_type,
      is_global:
        save_type === "all_companies" ? true : record?.is_global ?? false,
      update_related_data: updateRelatedData,
    };

    mutate(
      {
        payload,
        ...(!isNaN(Number(id)) ? { id } : {}),
      },
      {
        onSuccess() {},
        onError(e: any) {
          notification["error"]({
            message:
              handleMessageError(e?.response?.data?.category_status) ||
              e?.response?.data?.message ||
              e?.response?.data?.error ||
              "更新に失敗しました。",
          });
        },
        onSettled() {
          onCloseModalConfirm();
          setModalEditConfirm((prev) => ({
            ...prev,
            isOpen: false,
            recordId: null,
            originalName: "",
            newName: "",
            updateRelatedData: false,
            saveType: "current_company",
          }));
          saveTypeRef.current = "current_company";
        },
      },
    );
  };

  const onCloseModalConfirm = () => {
    setModalConfirm({
      isOpen: false,
      callback: null,
      content: "",
    });
    saveTypeRef.current = "current_company";
  };

  const handleNameBlur = (
    id: any,
    newValue: string,
    record: CategoryStatus,
  ) => {
    // Prevent name update for NULL option
    if (isOptionNull(record)) {
      return;
    }

    setTimeout(() => {
      // Skip if modal is already open for this record
      if (modalEditConfirm.isOpen && modalEditConfirm.recordId === id) {
        return;
      }

      const recordOriginal = categoryStatusesData?.data?.find(
        (i) => i?.id === id,
      );

      if (isNaN(Number(id))) {
        return;
      }

      if (recordOriginal?.name === newValue) {
        return;
      }

      if (
        record?.can_be_deleted === false &&
        recordOriginal?.name !== newValue
      ) {
        const nextSaveType = saveTypeRef.current || "current_company";
        setModalEditConfirm((prev) => ({
          ...prev,
          isOpen: true,
          recordId: id,
          originalName: recordOriginal?.name || "",
          newName: newValue,
          updateRelatedData: true,
          saveType: nextSaveType,
        }));
      }
    }, 0);
  };

  const handleConfirmEdit = (
    overrideSaveType?: "current_company" | "all_companies",
  ) => {
    if (modalEditConfirm.recordId) {
      const effectiveSaveType =
        overrideSaveType ||
        modalEditConfirm.saveType ||
        saveTypeRef.current ||
        "current_company";

      handleChangeData(
        "name",
        modalEditConfirm.recordId,
        modalEditConfirm.newName,
      );

      onApplyForCompany(
        modalEditConfirm.recordId,
        effectiveSaveType,
        modalEditConfirm.newName,
        modalEditConfirm.updateRelatedData,
      );
    }
  };

  const handleCancelEdit = () => {
    // Revert name to original
    if (modalEditConfirm.recordId) {
      handleChangeData(
        "name",
        modalEditConfirm.recordId,
        modalEditConfirm.originalName,
      );
      // Show error message but don't disable edit
      notification["warning"]({
        message: "当該値は使用中のため、編集できません。",
        style: {
          maxWidth: "unset",
          minWidth: 420,
          width: "auto",
          whiteSpace: "nowrap",
        },
      });
    }
    setModalEditConfirm((prev) => ({
      ...prev,
      isOpen: false,
      recordId: null,
      originalName: "",
      newName: "",
      updateRelatedData: false,
      saveType: "current_company",
    }));
    saveTypeRef.current = "current_company";
  };

  const handleSetSaveType = (type: "current_company" | "all_companies") => {
    saveTypeRef.current = type;
  };

  const columns: any = [
    {
      title: "項目名",
      dataIndex: "name",
      key: "name",
      render: (value: string, record: CategoryStatus) => {
        const isError = dataSource
          ?.filter((i) => i?.id !== record?.id && record?.name)
          ?.map((i) => i?.name)
          ?.includes(value);

        const isNull = isOptionNull(record);

        return (
          <Input
            value={value}
            {...(isError
              ? {
                  status: "error",
                }
              : {})}
            disabled={nonEditable || !!record?.deleted_at || isNull}
            className={record?.deleted_at ? "deleted-record" : ""}
            onChange={(e) => {
              if (isNull) return;
              // Allow user to type freely
              handleChangeData("name", record?.id, e?.target?.value);
            }}
            onBlur={(e) => {
              if (isNull) return;
              // Check when user leaves the input field
              handleNameBlur(record?.id, e.target.value, record);
            }}
          />
        );
      },
    },
    {
      title: "表示設定",
      dataIndex: "display_settings",
      key: "display_settings",
      width:
        displaySize === DISPLAY_SIZES.LARGE
          ? 250
          : displaySize === DISPLAY_SIZES.MEDIUM
          ? 220
          : 200,
      render: (_: any, record: CategoryStatus) => {
        return (
          <>
            <Checkbox
              value={displayCategoryStatusSettingsOptions[0]?.value}
              checked={
                !(record as any)?.[
                  displayCategoryStatusSettingsOptions[0]?.value
                ]
              }
              disabled={!!record?.deleted_at || nonEditable}
              onChange={(e) => {
                if (
                  e?.target?.checked === false &&
                  screen === "project_manage"
                ) {
                  handleChangeData(
                    displayCategoryStatusSettingsOptions[1]?.value as any,
                    record?.id,
                    false,
                  );
                }
                handleChangeData(
                  displayCategoryStatusSettingsOptions[0]?.value as any,
                  record?.id,
                  !e?.target?.checked,
                );
              }}
            >
              {displayCategoryStatusSettingsOptions[0]?.label}
            </Checkbox>
            <Checkbox
              value={displayCategoryStatusSettingsOptions[1]?.value}
              checked={
                (record as any)?.[
                  displayCategoryStatusSettingsOptions[1]?.value
                ]
              }
              disabled={
                nonEditable ||
                !!record?.deleted_at ||
                (record as any)?.[
                  displayCategoryStatusSettingsOptions[0]?.value
                ]
              }
              onChange={(e) =>
                handleChangeData(
                  displayCategoryStatusSettingsOptions[1]?.value as any,
                  record?.id,
                  e?.target?.checked,
                )
              }
            >
              {displayCategoryStatusSettingsOptions[1]?.label}
            </Checkbox>
          </>
        );
      },
    },
    {
      title: "削除",
      dataIndex: "delete",
      key: "delete",
      width: 200,
      render: (_: any, record: CategoryStatus) => {
        const isInprogressing = checkCategoryStatusIsInprogressing(
          record?.status,
        );

        if (isInprogressing) {
          return <></>;
        }

        const isNull = isOptionNull(record);

        const handleOnClick = () => {
          if (!nonEditable && !isNull) {
            // Check if record can be deleted
            if (
              record?.can_be_deleted === false ||
              record?.category_status_used
            ) {
              notification["error"]({
                message: "現在使用中のため、削除できません。",
              });
              return;
            }

            if (isFinite(Number(record?.id))) {
              setModalConfirm({
                isOpen: true,
                callback: () => handleDeleteRow(record?.id),
                content:
                  "この選択肢は一時的に削除されます。\n 後で復元することができます。\n 本当に削除してもよろしいですか？",
              });
            } else {
              handleDeleteRow(record?.id);
            }
          }
        };

        return !record?.deleted_at ? (
          <div className="d-flex">
            <Button
              className="delete-tag"
              label={isFinite(Number(record?.id)) ? "一時削除" : "選択肢削除"}
              onClick={handleOnClick}
              type="delete-btn"
              disabled={nonEditable || isNull}
              style={{
                fontSize: size,
                width: "fit-content",
              }}
              displaySize={displaySize}
            />
          </div>
        ) : (
          <div className="d-flex">
            <Button
              label="復元"
              className="delete-tag restore mr-1"
              disabled={nonEditable || isNull}
              onClick={() =>
                !nonEditable &&
                !isNull &&
                setModalConfirm({
                  isOpen: true,
                  callback: () => handleRestore(record?.id),
                  content: "復元してもよろしいですか？",
                })
              }
              style={{
                fontSize: size,
                width: "fit-content",
              }}
              displaySize={displaySize}
            />
            <Button
              label="選択肢削除"
              className="delete-tag"
              type="delete-btn"
              disabled={
                nonEditable || record?.can_be_deleted === false || isNull
              }
              onClick={() => {
                if (!nonEditable && !isNull) {
                  // Check if record can be deleted
                  if (
                    record?.can_be_deleted === false ||
                    record?.category_status_used
                  ) {
                    notification["error"]({
                      message: "現在使用中のため、削除できません。",
                    });
                    return;
                  }
                  setModalConfirm({
                    isOpen: true,
                    callback: () => handleDeleteRow(record?.id),
                    content:
                      "この選択肢は完全に削除されます。\n 空白になり、元に戻せません。\n 本当に削除してもよろしいですか？",
                  });
                }
              }}
              displaySize={displaySize}
              style={{
                fontSize: size,
                width: "fit-content",
              }}
            />
          </div>
        );
      },
    },
    {
      title: "保存/展開",
      dataIndex: "save_deploy",
      key: "save_deploy",
      width: 230,
      render: (_: any, record: CategoryStatus) => {
        const recordOriginal = categoryStatusesData?.data?.find(
          (i) => i?.id === record?.id,
        );
        const isChanged =
          recordOriginal?.name !== record?.name ||
          recordOriginal?.is_auto_select !== record?.is_auto_select ||
          recordOriginal?.is_hidden !== record?.is_hidden;

        const isInprogressing = checkCategoryStatusIsInprogressing(
          record?.status,
        );

        return (
          <div className="d-flex">
            <Button
              label="一時保存"
              className="mr-2 custom-btn-category-status"
              type="submit-btn"
              onMouseDown={() => handleSetSaveType("current_company")}
              onClick={() => {
                if (nonEditable) return;
                setModalConfirm({
                  isOpen: true,
                  callback: () => handleDeployment(record?.id),
                  content: "一時保存してもよろしいですか？",
                });
              }}
              disabled={
                !(
                  isNaN(Number(record?.id)) || // no have record_id => is create new
                  (isChanged && !record?.is_global)
                ) ||
                !!record?.deleted_at ||
                isInprogressing
              }
              displaySize={displaySize}
              style={{
                fontSize: size,
                width: "fit-content",
                height: `${height}px`,
              }}
            />

            <Button
              label="全社展開"
              type="submit-btn"
              className="custom-btn-category-status"
              onMouseDown={() => handleSetSaveType("all_companies")}
              onClick={() => {
                if (nonEditable) return;
                const recordOriginal = categoryStatusesData?.data?.find(
                  (i) => i?.id === record?.id,
                );
                // Check if name has changed and can_be_deleted is false
                if (
                  record?.can_be_deleted === false &&
                  recordOriginal?.name !== record?.name
                ) {
                  // Show edit confirmation modal first
                  setModalEditConfirm((prev) => ({
                    ...prev,
                    isOpen: true,
                    recordId: record?.id,
                    originalName: recordOriginal?.name || "",
                    newName: record?.name,
                    updateRelatedData: true,
                    saveType: "all_companies",
                  }));
                } else {
                  // Show normal confirmation modal
                  setModalConfirm({
                    isOpen: true,
                    callback: () =>
                      onApplyForCompany(record?.id, "all_companies"),
                    content: "全社展開してもよろしいですか？",
                  });
                }
              }}
              disabled={
                isNaN(Number(record?.id)) ||
                (record?.is_global && !isChanged) ||
                !!record?.deleted_at ||
                isInprogressing ||
                nonEditable
              }
              displaySize={displaySize}
              style={{
                fontSize: size,
                width: "fit-content",
                height: `${height}px`,
              }}
            />
          </div>
        );
      },
    },
    {
      title: "ステータス",
      dataIndex: "status",
      key: "status",
    },
  ];

  useEffect(() => {
    if (isLoading) {
      setDataSource([]);
    } else {
      if (isSuccess) {
        setDataSource(categoryStatusesData?.data);
      }
    }
  }, [isFetching, isLoading, isSuccess, categoryStatusesData]);

  return {
    columns,
    dataSource,
    handleAddNewRow,
    recordType,
    screen,
    isSubmitting,
    isFetching,
    isLoading:
      isLoading || isRestoringCategoryStatus || isDeletingCategoryStatus,
    nonEditable,
    refetch,
    modalConfirm,
    onCloseModalConfirm,
    modalEditConfirm,
    handleConfirmEdit,
    handleCancelEdit,
    displaySize,
    height,
    size,
  };
};

export type Props = ReturnType<typeof useCategoryStatusTable>;

export default useCategoryStatusTable;
